import React, { useState, useEffect } from 'react';
import { Package, Printer, Loader2, Bluetooth } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { printThermalReceipt } from '@/utils/thermalPrinter';
import { printBluetoothReceipt, getPrinterInstance } from '@/utils/bluetoothPrinter';
import { getPrendasRecoleccion } from '../services/deposito.services';

const ModalDetalleRecoleccion = ({ isOpen, onClose, recoleccion }) => {
  const { toast } = useToast();
  const [prendas, setPrendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);

  useEffect(() => {
    if (isOpen && recoleccion) {
      loadPrendas();
      checkPrinterConnection();
    }
  }, [isOpen, recoleccion]);

  const checkPrinterConnection = () => {
    const printer = getPrinterInstance();
    setPrinterConnected(printer.isConnected());
  };

  const loadPrendas = async () => {
    setLoading(true);
    try {
      // Buscar prendas en la colección de prendas_recoleccion
      const recoleccionId = recoleccion._id || recoleccion.id;
      const response = await getPrendasRecoleccion(recoleccionId);
      setPrendas(response?.data || []);
    } catch (error) {
      console.error('Error cargando prendas:', error);
      // Si falla, intentar usar las prendas que vienen en la recolección
      if (recoleccion.prendas && Array.isArray(recoleccion.prendas)) {
        setPrendas(recoleccion.prendas);
      } else {
        setPrendas([]);
      }
      toast({
        title: 'Advertencia',
        description: 'Mostrando prendas del cache local',
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'recibido': { color: 'text-blue-600 border-blue-600', label: 'Recibido' },
      'pendiente_verificacion': { color: 'text-yellow-600 border-yellow-600', label: 'Pendiente Verificación' },
      'verificado': { color: 'text-green-600 border-green-600', label: 'Verificado' },
      'rechazado': { color: 'text-red-600 border-red-600', label: 'Rechazado' },
      'entregado': { color: 'text-purple-600 border-purple-600', label: 'Entregado' }
    };
    const badge = badges[estado] || { color: '', label: 'Desconocido' };
    return <Badge variant="outline" className={badge.color}>{badge.label}</Badge>;
  };

  const handlePrint = () => {
    try {
      printThermalReceipt(recoleccion);
      toast({
        title: 'Imprimiendo',
        description: 'Enviando recibo a la impresora...',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo imprimir el recibo',
        variant: 'destructive',
      });
    }
  };

  const handleBluetoothPrint = async () => {
    setPrinting(true);
    try {
      // Crear objeto completo con prendas para imprimir
      const recoleccionCompleta = {
        ...recoleccion,
        prendas: prendas
      };

      await printBluetoothReceipt(recoleccionCompleta);
      
      // Actualizar estado de conexión
      setPrinterConnected(true);
      
      toast({
        title: 'Impresión exitosa',
        description: 'El recibo se imprimió correctamente en tu PT-210',
      });
    } catch (error) {
      console.error('Error en impresión Bluetooth:', error);
      
      let errorMessage = 'No se pudo imprimir el recibo';
      
      if (error.message.includes('Bluetooth')) {
        errorMessage = 'Tu navegador no soporta Bluetooth. Usa Chrome, Edge u Opera en Android.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna impresora. Asegúrate de que tu PT-210 esté encendida y en modo emparejamiento.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Permiso denegado. Debes permitir el acceso a Bluetooth.';
      } else if (error.message.includes('conexión')) {
        errorMessage = 'Conexión perdida. Verifica que la PT-210 esté encendida y cerca del dispositivo.';
      }
      
      // Actualizar estado de conexión
      setPrinterConnected(false);
      
      toast({
        title: 'Error de impresión',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setPrinting(false);
    }
  };

  if (!recoleccion) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Package className="h-5 w-5" />
              {recoleccion?.consecutivo || 'Recolección'}
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBluetoothPrint}
                disabled={printing}
                className={`flex items-center gap-2 ${printerConnected ? 'text-green-600' : ''}`}
                title={printerConnected ? "Imprimir en PT-210 (Conectada)" : "Imprimir vía Bluetooth"}
              >
                {printing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Bluetooth className={`h-4 w-4 ${printerConnected ? 'text-green-600' : ''}`} />
                )}
                {printerConnected && <span className="text-xs">PT-210</span>}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrint}
                className="flex items-center gap-2"
                title="Imprimir (PC)"
              >
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Información compacta */}
          <div className="grid grid-cols-3 gap-3 p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground">Estado</p>
              <div className="mt-1">{getEstadoBadge(recoleccion.estado)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Empleado</p>
              <p className="text-sm font-medium">{recoleccion.empleado?.nombre || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Cédula</p>
              <p className="text-sm font-medium">{recoleccion.empleado?.cedula || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Fecha</p>
              <p className="text-sm font-medium">
                {recoleccion.createdAt 
                  ? new Date(recoleccion.createdAt).toLocaleDateString('es-CO')
                  : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Área</p>
              <p className="text-sm font-medium">{recoleccion.empleado?.area?.nombre || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-sm font-bold">{recoleccion.cantidad_prendas || 0} prendas</p>
            </div>
          </div>

          {/* Prendas */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Prendas</h3>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Cargando prendas...
              </div>
            ) : prendas.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {prendas.map((prenda, index) => {
                  const total      = prenda.cantidad || 0;
                  const entregadas = prenda.cantidad_entregada || 0;
                  const pendientes = prenda.cantidad_pendiente ?? total;
                  const completa   = pendientes === 0;
                  return (
                    <div
                      key={prenda._id || prenda.id || index}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        completa
                          ? 'border-green-200 bg-green-50/50 dark:bg-green-900/5'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs text-muted-foreground w-5 shrink-0">{index + 1}.</span>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">
                            {prenda.nombre || 'Sin nombre'}
                          </p>
                          {prenda.observaciones && (
                            <p className="text-xs text-muted-foreground truncate">{prenda.observaciones}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0 ml-3 text-xs">
                        <span className="text-muted-foreground">
                          Total: <strong className="text-foreground">{total}</strong>
                        </span>
                        <span className="text-muted-foreground">
                          Entregadas: <strong className="text-green-600">{entregadas}</strong>
                        </span>
                        <Badge
                          variant="outline"
                          className={completa ? 'text-green-600 border-green-400' : 'text-orange-600 border-orange-400'}
                        >
                          {completa ? 'Completa' : `${pendientes} pendiente${pendientes !== 1 ? 's' : ''}`}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hay prendas registradas</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose} size="sm">
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalDetalleRecoleccion;
